import React, { useCallback, useState, useEffect } from 'react';
import { formatTranslation } from 'intl';
import { InspectionTranslationKeys, InspectionTemplateTranslationKeys } from 'intl/keys';
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Tabs,
  Tab,
} from '@mui/material';
import { useIntl } from 'react-intl';
import { GraphicUploader } from 'components/common/graphicUploader';
import { ATTACHMENTS_UPLOADER_PRESET } from 'components/common/graphicUploader/attachmentsPreset';
import { useGraphicUploader } from 'components/common/graphicUploader/useLocalGraphicUploader';
import { useInspectionTemplate } from 'store/inspection';
import { useAppUrlParams, UrlParamName } from 'hooks/navigation/useAppUrlParams';
import { createTemplateChecklistItemDocumentsBaseUrl } from 'shared/domain/template/templateDocumentPaths';
import { DocumentToChecklistFromUrlBinder } from 'shared/domain/document/documentBinder';
import { MemoChecklistTreeNodeHeader } from 'components/common/checklistTree';
import { MemoValidatedTextField } from 'components/common/forms/fields/ValidatedTextField';
import { RichTextEditor } from 'components/common/RichTextEditor';
import { CollapsibleRichTextComment } from 'components/inspection/CollapsibleRichTextComment';
import { InspectionCommentLabelVariant } from 'components/inspection/CollapsibleInspectionComment/types';
import { AnswerType } from 'shared/domain/checklistAnswer/types/answerType';
import { MemoAnswerConfigList, AnswerConfig } from './answerConfig';
import {
  ACCORDION_SX,
  ACCORDION_SUMMARY_SX,
  ACCORDION_DETAILS_SX,
} from './styles';
import { ChecklistItemNodeProps } from './types';
import { TABS_CONTAINER_SX, getChecklistItemTabSx } from './answerConfig/styles';
import { getChecklistItemDomElementId } from 'views/template/wizard/form/helpers';
import { hasAnswersTabErrors } from 'views/template/wizard/form/validation';

type TabValue = 'basic' | 'answers';

function add0ToSingleDigitNumber(num: number): string {
  return num < 10 ? `0${num}` : `${num}`;
}

function hasItemErrors(errors: ChecklistItemNodeProps['errors']): boolean {
  return Boolean(errors?.summary || errors?.description || hasAnswersTabErrors(errors?.answers));
}

/** An item with errors stays expanded: the user cannot collapse it before fixing them. */
function shouldBeExpanded(
  errors: ChecklistItemNodeProps['errors'],
  currentExpanded: boolean
): boolean {
  return hasItemErrors(errors) || currentExpanded;
}

export function ChecklistItemNode({
  node,
  onSummaryChange,
  onDescriptionChange,
  onGraphicUploaderInit,
  onDelete,
  errors,
  defaultValues,
  graphicUploader,
  dragHandleProps,
  index = 0,
  answers = [],
  onAddAnswer,
  onUpdateAnswer,
  onDeleteAnswer,
  validationFocus,
}: ChecklistItemNodeProps): React.ReactElement {
  const intl = useIntl();
  const inspectionTemplateId = useAppUrlParams(
    UrlParamName.INSPECTION_TEMPLATE_ID
  );
  const { inspectionTemplateDocuments } = useInspectionTemplate();
  const [expanded, setExpanded] = useState(true);
  const [activeTab, setActiveTab] = useState<TabValue>('basic');
  const { data } = node;
  const checklistItemDocuments = React.useMemo(
    () => inspectionTemplateDocuments.checklistDocuments[node.id] || [],
    [inspectionTemplateDocuments.checklistDocuments, node.id]
  );
  const localGraphicUploader = useGraphicUploader(checklistItemDocuments, {
    ...ATTACHMENTS_UPLOADER_PRESET,
    baseUrl: createTemplateChecklistItemDocumentsBaseUrl(
      inspectionTemplateId,
      node.id
    ),
    atomicChanges: true,
    dataQaPrefix: `checklist-item-${node.id}-`,
    documentBinderFactory: (baseUrl?: string) => {
      if (!baseUrl) {
        throw new Error('Checklist uploader requires baseUrl');
      }
      return new DocumentToChecklistFromUrlBinder(baseUrl);
    },
  });
  const graphicUploaderToUse = graphicUploader || localGraphicUploader;

  // Errors open the item during render; it stays open once they are fixed.
  if (hasItemErrors(errors) && !expanded) {
    setExpanded(true);
  }

  useEffect(() => {
    if (validationFocus?.itemId !== node.id) {
      return;
    }

    setExpanded(true);
    setActiveTab(validationFocus.tab);
  }, [validationFocus, node.id]);

  useEffect(() => {
    if (!graphicUploader && onGraphicUploaderInit) {
      onGraphicUploaderInit(node.id, localGraphicUploader);
    }
  }, [
    graphicUploader,
    onGraphicUploaderInit,
    node.id,
    localGraphicUploader,
  ]);

  const handleExpandToggle = useCallback(() => {
    setExpanded((prev) => shouldBeExpanded(errors, !prev));
  }, [errors]);

  const handleSummaryChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (onSummaryChange) {
        onSummaryChange(node.id, event.target.value);
      }
    },
    [node.id, onSummaryChange]
  );

  const handleDescriptionChange = useCallback(
    (html: string) => {
      if (onDescriptionChange) {
        onDescriptionChange(node.id, html);
      }
    },
    [node.id, onDescriptionChange]
  );

  const handleDelete = useCallback(() => {
    if (onDelete) {
      onDelete(node.id);
    }
  }, [node.id, onDelete]);

  const handleTabChange = useCallback(
    (_: React.SyntheticEvent, newValue: TabValue) => {
      setActiveTab(newValue);
    },
    []
  );

  const handleAddAnswer = useCallback(
    (answerType: AnswerType) => {
      if (onAddAnswer) {
        onAddAnswer(node.id, answerType);
      }
    },
    [node.id, onAddAnswer]
  );

  const handleUpdateAnswer = useCallback(
    (answerId: string, updates: Partial<AnswerConfig>) => {
      if (onUpdateAnswer) {
        onUpdateAnswer(node.id, answerId, updates);
      }
    },
    [node.id, onUpdateAnswer]
  );

  const handleDeleteAnswer = useCallback(
    (answerId: string) => {
      if (onDeleteAnswer) {
        onDeleteAnswer(node.id, answerId);
      }
    },
    [node.id, onDeleteAnswer]
  );

  const displayNumber = node.displayNumber || add0ToSingleDigitNumber(index + 1);
  const summaryText = data.summary || formatTranslation(intl, InspectionTemplateTranslationKeys.wizardItemPlaceholder);
  const hasSummary = Boolean(data.summary);
  const hasBasicTabError = Boolean(errors?.summary || errors?.description);
  const hasAnswersTabError = hasAnswersTabErrors(errors?.answers);

  return (
    <Box sx={{ mb: 1 }} id={getChecklistItemDomElementId(node.id)}>
      <Accordion
        expanded={expanded}
        onChange={(_, isExpanded) => setExpanded(shouldBeExpanded(errors, isExpanded))}
        sx={ACCORDION_SX}
      >
        <AccordionSummary expandIcon={false} sx={ACCORDION_SUMMARY_SX}>
          <MemoChecklistTreeNodeHeader
            displayNumber={displayNumber}
            title={summaryText}
            expanded={expanded}
            onExpandToggle={handleExpandToggle}
            onDelete={onDelete ? handleDelete : undefined}
            dragHandleProps={dragHandleProps}
            titleFontWeight={hasSummary ? 500 : 400}
            titleColor={hasSummary ? 'text.primary' : 'text.secondary'}
            titleFontStyle={hasSummary ? 'normal' : 'italic'}
          />
        </AccordionSummary>
        <AccordionDetails sx={ACCORDION_DETAILS_SX}>
          {onAddAnswer && (
            <Box sx={TABS_CONTAINER_SX}>
              <Tabs value={activeTab} onChange={handleTabChange}>
                <Tab
                  value="basic"
                  label={formatTranslation(intl, InspectionTranslationKeys.tabBasic)}
                  sx={getChecklistItemTabSx(hasBasicTabError)}
                />
                <Tab
                  value="answers"
                  label={formatTranslation(intl, InspectionTranslationKeys.tabAnswers)}
                  sx={getChecklistItemTabSx(hasAnswersTabError)}
                />
              </Tabs>
            </Box>
          )}

          {activeTab === 'basic' && (
            <Stack spacing={2}>
              <MemoValidatedTextField
                label={formatTranslation(intl, InspectionTemplateTranslationKeys.wizardChecklistItemTitle)}
                variant="outlined"
                size="medium"
                multiline
                onChange={handleSummaryChange}
                required
                fieldError={errors?.summary}
                value={data.summary}
                fullWidth
                inputProps={{ maxLength: 150 }}
              />

              <CollapsibleRichTextComment
                value={data.norm?.description ?? ''}
                labelVariant={InspectionCommentLabelVariant.floating}
                floatingLabelId={
                  InspectionTemplateTranslationKeys.wizardSpecifiedAppliedStandardDescription
                }
              >
                <RichTextEditor
                  value={data.norm?.description ?? ''}
                  onChange={handleDescriptionChange}
                  error={Boolean(errors?.description)}
                  maxLength={10000}
                  minRows={3}
                  dense
                  placeholder={formatTranslation(
                    intl,
                    InspectionTranslationKeys.commentAddPlaceholder
                  )}
                />
              </CollapsibleRichTextComment>

              <GraphicUploader {...graphicUploaderToUse} />
            </Stack>
          )}

          {activeTab === 'answers' && onAddAnswer && (
            <MemoAnswerConfigList
              answers={answers}
              answerErrors={errors?.answers}
              onAddAnswer={handleAddAnswer}
              onUpdateAnswer={handleUpdateAnswer}
              onDeleteAnswer={handleDeleteAnswer}
            />
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

export const MemoChecklistItemNode = React.memo(ChecklistItemNode);
